import { createElement } from 'react';
import { Switch } from '@mui/material';
import moment from 'moment';

import { capitalizeFirstLetter } from '../../helpers';

import type { ClinicalStatus, ProviderClinicalStatus } from '../constants/general';
import type { MUIDataTableMeta } from 'mui-datatables';
import type { ChangeEvent, MouseEvent } from 'react';

export const getClientColumns = (showClinicalStatus = true) => [
  {
    name: 'id',
    label: 'CID',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'name',
    label: 'Name',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'sex',
    label: 'Gender',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'dob',
    label: 'DOB',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: any) => {
        return moment(value)?.format('MM-DD-YYYY');
      },
    },
  },
  {
    name: 'clinical_status',
    label: 'Clinical Status',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: ClinicalStatus) => {
        return capitalizeFirstLetter(value.toLowerCase());
      },
      display: showClinicalStatus,
    },
  },
  {
    name: 'email',
    label: 'Email',
    options: {
      filter: true,
      sort: true,
      display: false,
    },
  },
  {
    name: 'phone',
    label: 'Phone',
    options: {
      filter: true,
      sort: true,
      display: false,
    },
  },
];

export const getProviderColumns = (showClinicalStatus = true) => [
  {
    name: 'id',
    label: 'PID',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'name',
    label: 'Name',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'email',
    label: 'Email',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'phone',
    label: 'Phone',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'sex',
    label: 'Gender',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'dob',
    label: 'DOB',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: any) => {
        return moment(value)?.format('MM-DD-YYYY');
      },
    },
  },
  {
    name: 'roles',
    label: 'Roles',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'clinical_status',
    label: 'Clinical status',
    options: {
      filter: true,
      sort: true,
      customBodyRender: (value: ProviderClinicalStatus) => {
        return capitalizeFirstLetter(value.toLowerCase());
      },
      display: showClinicalStatus,
    },
  },
];

export const facilityColumns = [
  {
    name: 'id',
    label: 'BFID',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'label',
    label: 'Label',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'npi',
    label: 'NPI',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'tin',
    label: 'TIN',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'group_medicaid_id',
    label: 'Medicaid Id',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'street_address_line_one',
    label: 'Address 1',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'street_address_line_two',
    label: 'Address 2',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'street_address_state',
    label: 'State',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'street_address_zip',
    label: 'ZIP',
    options: {
      filter: true,
      sort: true,
    },
  },
];

export const payorColumns = [
  {
    name: 'id',
    label: 'PID',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'label',
    label: 'Label',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'state',
    label: 'State',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'modifier',
    label: 'Modifier',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'preferred_service_code_type',
    label: 'Service Code Type',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'taxonomy_code',
    label: 'Taxonomy Code',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'type',
    label: 'Payor Type',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'clearinghouse_payor_id',
    label: 'Clearing House Payor Id',
    options: {
      filter: true,
      sort: true,
    },
  },
];

export const goalTemplateColumns = [
  {
    name: 'id',
    label: 'Gid',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'label',
    label: 'Goal Name',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'prefix',
    label: 'Prefix',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'is_active',
    label: 'Is Active',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'behavior_id',
    label: 'Fid',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'focus_area',
    label: 'Focus Area',
    options: {
      filter: true,
      sort: true,
    },
  },
];

export const cohortColumns = [
  {
    name: 'id',
    label: 'CID',
    options: {
      filter: true,
      sort: true,
    },
  },
  {
    name: 'name',
    label: 'Name',
    options: {
      filter: true,
      sort: true,
    },
  },

  {
    name: 'service_classes',
    label: 'Service Classes',
    options: {
      filter: true,
      sort: true,
    },
  },
];

// Pure column config — the Redux toggle side effect lives in the
// `useEmailReports` hook and is passed in as a typed callback.
export const getEmailReportsColumns = (onToggleEnabled: (id: number, enabled: boolean) => void) => {
  return [
    {
      name: 'id',
      label: 'ID',
      options: {
        filter: false,
        sort: true,
        display: false,
      },
    },
    {
      name: 'job_key',
      label: 'Job',
      options: {
        filter: true,
        sort: true,
      },
    },
    {
      name: 'recipients',
      label: 'Recipients',
      options: {
        filter: false,
        sort: false,
        customBodyRender: (value: string[] | null) => (value || []).join(', ') || '(default)',
      },
    },
    {
      name: 'is_runnable',
      label: 'Runnable',
      options: {
        filter: true,
        sort: true,
        customBodyRender: (value: boolean) => (value ? '✓' : 'not runnable'),
      },
    },
    {
      name: 'enabled',
      label: 'Enabled',
      options: {
        filter: true,
        sort: false,
        customBodyRender: (value: boolean, tableMeta: MUIDataTableMeta) => {
          const id = Number(tableMeta.rowData?.[0]);
          return createElement(Switch, {
            checked: Boolean(value),
            onClick: (e: MouseEvent) => e.stopPropagation(),
            onChange: (e: ChangeEvent<HTMLInputElement>, checked: boolean) => {
              e.stopPropagation();
              onToggleEnabled(id, checked);
            },
          });
        },
      },
    },
  ];
};
